/**
 * Os Produtos do Negócio, dentro da ficha (5.6, FR-44, FR-45, FR-46, FR-47, FR-62, AD-19).
 *
 * **Ele é a resposta à ausência que a 3.10 registrou por escrito.** Aquela história disse que a
 * ficha "não edita `valor` nem Produtos, porque o preço é travado pelo ERP e as linhas são do Épico
 * 5"; este cartão é o Épico 5 chegando — e a promessa se cumpre inteira: o preço continua sem
 * caminho de digitação, e o que a tela ganhou foi o **produto**, não o campo de valor.
 *
 * **Não há campo de preço, e não há campo de desconto.** O preço é renderizado como **texto**,
 * nunca como `input`, e nenhum formulário desta tela o envia: quem o escreve é o Carga Máquina, e o
 * contrato do serviço não tem por onde recebê-lo (FR-45). É por essa porta que desconto por fora e
 * custo à mostra entrariam (FR-44, AD-19), e a ausência dela é o desenho.
 *
 * **Não há catálogo de Produtos, e a busca mora aqui dentro.** Toda pergunta ao ERP é por
 * `{negocio_id}`: não existe tela de Produtos fora do Negócio, e não vai existir por esta porta — o
 * hook de busca é o do `modulos/integracao-erp`, e o cruzamento é o AD-25 escrito em import, como o
 * cartão de Orçamento já faz na direção contrária.
 *
 * **Abrir o cartão relê os preços, e a tela não precisa saber disso.** O `GET` do serviço regrava as
 * linhas quando o Negócio está em andamento (AD-19); daqui, ele é uma leitura como outra qualquer.
 * O que muda é a consequência: mudou o preço no ERP, a lista abre com o número novo **e** o
 * cabeçalho da ficha passa a somar outro total — por isso as mutações invalidam a chave da ficha
 * junto (`api.ts`).
 *
 * **Três estados de leitura, e os três estão escritos.** Falha do serviço vira a mensagem dele,
 * verbatim, em `role="alert"` — inclusive o `503` do FR-47, que é a frase que a tela **não**
 * reescreve; enquanto a resposta não chegou, um `role="status"` diz que está carregando; lista
 * vazia é o estado vazio. Sem o terceiro, "não há produto" e "ainda não sei" ficariam iguais.
 *
 * **O ERP fora do ar não some com o cartão do Negócio fechado.** Ganho ou perdido, o serviço devolve
 * o que foi congelado sem tocar o Carga Máquina — e é justamente aí que a lista mais importa, porque
 * ela é a composição da venda que aconteceu.
 *
 * **A disponibilidade é informação, e não dinheiro (13.22a1).** Ela vem no mesmo `GET` que relê os
 * preços — o serviço lê o estoque do Carga Máquina no mesmo salto —, e por isso só existe no Negócio
 * em andamento: fechado, a coluna some (o serviço manda `null` e não toca o ERP). Em andamento, o
 * estoque que não respondeu aparece "não consultada", e o preço ao lado continua o de sempre. Ela
 * chega a todo perfil, inclusive à SDR, e nunca é gravada: quem a congela é o orçamento (13.22a2).
 *
 * **O dinheiro é lido pelos três estados de `textoDoValor`**, como no cartão de Orçamento: chave
 * ausente é o recorte da SDR (FR-62), `null` é "o ERP não tem preço para este Material" e o número é
 * o preço. A função é a **mesma** que a ficha usa — duas cópias divergiriam na primeira mudança de
 * texto, e a ficha diria uma coisa sobre o recorte e este cartão, ao lado, outra.
 *
 * **Duas árvores, escolhidas no componente — e não um `@media`.** No computador as linhas são
 * uma tabela com cabeçalho; no celular, blocos com rótulo em cima do valor. Desde a 13.22a1 os
 * blocos valem também para o **cartão estreito** do computador (`useCartaoEstreito`): a ficha o põe
 * na coluna de 310 px do protótipo v2, e uma tabela de cinco colunas de número não cabe ali — o
 * Total e o Remover saíam cortados. É o mesmo desenho de
 * `OrcamentoDoNegocio`, e pela mesma razão: uma tabela de cinco colunas a 375 px vira rolagem
 * horizontal dentro de uma coluna que já rola. A paridade da NFR-1 é de **função**: acrescentar e
 * remover existem nas duas.
 *
 * **A escrita some para o Negócio fechado**, e a razão fica escrita ao lado: o serviço responde
 * `409` com a mesma frase de mover, editar e refechar, e o gatilho do banco é o piso. A tela não
 * adivinha a mensagem do serviço — ela escreve a própria, por conta própria, antes da ida à rede,
 * como a ficha já faz com os cinco controles que o desfecho bloqueia.
 *
 * **A busca tem dois campos, e eles não são um só repetido.** `GET /material` do Carga Máquina
 * combina `codigo` e `nome` por **E**: mandar o mesmo texto nos dois pede o Material cujo código
 * *e* nome contenham aquela string — que na prática não existe. Dois campos separados são o
 * contrato que a rota publica (os dois opcionais, ao menos um preenchido), e quem digita escolhe
 * por qual dos dois está procurando.
 *
 * **E ela é estabilizada antes de virar consulta.** Sem isso, a partir do terceiro caractere cada
 * tecla seria uma `queryKey` nova e portanto uma ida ao fornecedor: digitar "parafuso" custaria sete
 * chamadas ao Carga Máquina. O FR-46 diz que a chamada nasce de um ato concreto, e um teclado não é
 * um ato.
 */

import { type RefObject, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import {
  type FiltroDeMaterial,
  type MaterialDoErp,
  useBuscaDeMateriais,
} from '@/modulos/integracao-erp/api'
import {
  type ProdutoDoNegocio as Produto,
  useAcrescentarProduto,
  useProdutos,
  useRelerOValorDoNegocio,
  useRemoverProduto,
} from '@/modulos/negocios/api'
import { textoDoValor } from '@/modulos/negocios/apresentacao'
import { useEhCelular } from '@/shell/Shell'
import './produtos-do-negocio.css'

/** O nome acessível da seção — é ele que faz a `<section>` virar uma `region`. */
const ID_DO_TITULO = 'produtos-do-negocio-titulo'

const TITULO = 'Produtos'

/**
 * O selo do cartão, e ele **descreve** em vez de prometer.
 *
 * Ele é o irmão do "Espelho · leitura" do Orçamento, com uma diferença que importa: aqui há escrita
 * (acrescentar e remover), e o que é somente-leitura é o **preço**. O selo diz exatamente isso, e é
 * a primeira coisa que quem procura o campo de desconto lê.
 */
const SELO = 'Preço travado pelo ERP'

const CARREGANDO = 'Carregando os produtos…'

/**
 * O estado vazio: não há Produto **neste** Negócio, e de onde ele viria.
 *
 * Ele não anuncia épico nenhum — anunciar um dentro de um cartão que já entrega a funcionalidade
 * diria que ela não chegou. É a mesma troca que `SEM_TAREFAS` fez na 4.2.
 */
const VAZIO =
  'Nenhum produto neste negócio. Busque pelo código ou pelo nome para acrescentar — o preço vem ' +
  'do Carga Máquina.'
/** O vazio de quem não escreve (o fechado e a leitura pela tarefa, 13.14c): sem convidar a buscar. */
export const VAZIO_SEM_ESCRITA = 'Nenhum produto neste negócio.'

/**
 * A razão pela qual a escrita some no Negócio fechado — escrita **aqui**, e não adivinhada.
 *
 * O serviço responde `409` com a mensagem dele; esta é a da tela, e ela existe para que o controle
 * ausente tenha explicação ao lado em vez de sumir sem motivo. É o mesmo par controle-mais-frase que
 * a ficha usa nos cinco atos que o desfecho bloqueia.
 */
const NEGOCIO_FECHADO =
  'Negócio ganho ou perdido não muda de produto: o valor congelou no fechamento.'

/**
 * Os rótulos dos **dois** campos da busca, e eles são dois porque o ERP combina os filtros por E.
 *
 * Um campo só, mandado nos dois filtros, pede o Material cujo código *e* nome contenham o mesmo
 * texto — e a busca nunca acha nada. Separados, cada um vai sozinho na query, que é o que o
 * contrato da rota publica.
 */
const ROTULO_DO_CODIGO = 'Código do produto'
const ROTULO_DO_NOME = 'Nome do produto'
const AJUDA_DA_BUSCA =
  'Preencha um dos dois — os dois buscam por parte do texto, no catálogo do Carga Máquina, e o ' +
  'preço vem de lá.'
const ROTULO_DA_QUANTIDADE = 'Quantidade'

/**
 * Quantos caracteres a busca espera antes de ir à rede.
 *
 * Cada tecla seria uma ida ao Carga Máquina, e o FR-46 é sobre isso: a chamada nasce de um ato
 * concreto, e não de um teclado. Dois caracteres é o menor filtro que ainda recorta alguma coisa —
 * com um, `/material` devolveria a primeira página do catálogo inteiro.
 *
 * Ele é o piso do **tamanho**; quem cuida do ritmo é o debounce logo abaixo. Os dois são
 * necessários: sozinho, o mínimo deixa passar uma chamada por tecla a partir do terceiro caractere.
 */
const MINIMO_PARA_BUSCAR = 2

/**
 * Quanto o filtro fica parado antes de virar consulta, em milissegundos.
 *
 * Trezentos é a pausa entre teclas de quem digitou e parou para ler — abaixo disso, uma digitação
 * corrida ainda dispara no meio da palavra; muito acima, a lista parece travada depois de a pessoa
 * já ter terminado. E cada disparo evitado é uma chamada a menos ao fornecedor: "parafuso" custa
 * **uma** ida, e não sete.
 */
const ESPERA_DA_BUSCA_EM_MS = 300

const BUSCA_CURTA = `Digite ao menos ${String(MINIMO_PARA_BUSCAR)} caracteres para buscar.`
const BUSCANDO = 'Buscando no Carga Máquina…'
const SEM_RESULTADO = 'O Carga Máquina não tem produto com esse código ou nome.'

/**
 * A recusa **local** da quantidade, e ela existe para o `422` genérico nunca ser gasto.
 *
 * O campo vazio manda `{"quantidade": ""}`, que o serviço recusa com a mensagem genérica do
 * envelope ("Os dados enviados são inválidos.") — verdadeira e inútil, porque não diz qual campo.
 * Quantidade é `> 0` no contrato, no `CHECK` da coluna e aqui: o botão fica desabilitado enquanto
 * ela não for um número positivo, e a frase ao lado diz por quê.
 */
const QUANTIDADE_INVALIDA = 'Informe uma quantidade maior que zero para acrescentar.'

/** A quantidade com que o campo nasce, e à qual ele volta depois de cada acréscimo. */
const QUANTIDADE_PADRAO = '1'

/** Os rótulos das colunas, escritos uma vez e usados nas duas árvores. */
const ROTULOS = {
  produto: 'Produto',
  quantidade: 'Qtd.',
  disponivel: 'Disponível',
  preco: 'Preço unit.',
  total: 'Total',
} as const

/**
 * O que a coluna "Disponível" diz quando o estoque não foi lido agora (13.22a1).
 *
 * É a frase da tela, e não uma mensagem do servidor: o serviço manda `disponibilidade_consultada:
 * false`, e quem escolhe as palavras é aqui. "não consultada", e não um traço nem um zero: zero é um
 * estoque de verdade, e o traço diria "não há" a respeito de um número que ninguém leu.
 */
export const NAO_CONSULTADA = 'não consultada'

/**
 * A quantidade em pt-BR (NFR-8).
 *
 * Ela mora aqui, e não em `apresentacao.ts`: quantidade de linha de Produto é vocabulário **deste**
 * cartão, e o que as telas de Negócio compartilham é dinheiro e data. É a mesma decisão — e o mesmo
 * molde — de `QUANTIDADE` em `OrcamentoDoNegocio`, e as duas são cópias deliberadas: os módulos são
 * de serviços diferentes, e o que as amarra é o contrato, não um import.
 *
 * `maximumFractionDigits` é o teto do domínio (quatro casas): o serviço aceita até quatro na borda,
 * e um formatador com o padrão do `Intl` (três) arredondaria na tela um número que ele guardou
 * inteiro — a tela mentiria sobre o dado.
 */
export const QUANTIDADE = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 4,
})

/**
 * A menor largura do cartão, em pixels, em que a tabela de mesa cabe sem cortar coluna (13.22a1).
 *
 * São cinco colunas de número (Qtd., Disponível, Preço unit., Total e a ação) ao lado do Produto,
 * todas `nowrap`: abaixo disto, a última coluna sai cortada pela borda do cartão. Não é medida de
 * estilo — o CSS não a usa —, é o limiar da **troca de árvore**, como `LARGURA_MAXIMA_DO_CELULAR`
 * é o do shell.
 */
const LARGURA_MINIMA_DA_TABELA = 560

/**
 * Se o próprio cartão é estreito demais para a tabela, acompanhando a mudança de tamanho (13.22a1).
 *
 * A largura que importa é a **do cartão**, e não a da janela: no computador a ficha o põe na coluna
 * lateral de 310 px, e numa tela de 1600 px ele é tão estreito quanto no celular.
 *
 * **A primeira medida é antes de pintar** (code review da 13.22, F20): o `useLayoutEffect` lê a
 * largura do próprio cartão e decide a árvore antes do primeiro quadro — a coluna estreita já nasce
 * em blocos, sem piscar a tabela. Depois, o `ResizeObserver` acompanha. Sem medida nenhuma (largura
 * zero), vale a tabela, que é o desenho de antes.
 */
function useCartaoEstreito(): [RefObject<HTMLElement | null>, boolean] {
  const cartao = useRef<HTMLElement>(null)
  const [estreito, setEstreito] = useState(false)

  useLayoutEffect(() => {
    const elemento = cartao.current
    if (elemento === null) {
      return
    }
    const largura = elemento.getBoundingClientRect().width
    setEstreito(largura > 0 && largura < LARGURA_MINIMA_DA_TABELA)
    if (typeof ResizeObserver === 'undefined') {
      return
    }
    const observador = new ResizeObserver((entradas) => {
      const largura = entradas[0]?.contentRect.width ?? 0
      setEstreito(largura > 0 && largura < LARGURA_MINIMA_DA_TABELA)
    })
    observador.observe(elemento)
    return () => {
      observador.disconnect()
    }
  }, [])

  return [cartao, estreito]
}

/** A mensagem do serviço quando ela existe; a genérica quando a falha nem chegou a ter uma. */
function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/**
 * A quantidade em pt-BR; o texto verbatim quando ele não é número.
 *
 * O que não for número volta como veio, e não como `NaN`: o serviço promete um decimal em texto, e
 * um texto inesperado na tela é feio — um `NaN` é pior, porque parece um dado.
 */
function quantidadeNaTela(bruta: string): string {
  const numero = Number(bruta)
  return Number.isFinite(numero) ? QUANTIDADE.format(numero) : bruta
}

/**
 * O rótulo do Produto: o nome do ERP, e o **código** quando ele não veio.
 *
 * O traço seria a resposta errada aqui, e a diferença é o que a linha já traz: o `codigo` é a
 * identidade do Material no Carga Máquina — obrigatório no contrato, e a chave pela qual a releitura
 * reencontra o preço. Mostrar `—` sobre uma linha que carrega aquele código faria a tela dizer "não
 * sei que produto é este" ao lado do dado que responde exatamente isso. É o mesmo raciocínio de
 * `rotuloDoOrcamento`, no cartão ao lado.
 *
 * `nome` continua vindo primeiro porque é ele o rótulo **humano**: é o que o vendedor lê no catálogo
 * e repete ao telefone.
 */
function rotuloDoProduto(produto: { codigo: string; nome: string | null }): string {
  return produto.nome ?? produto.codigo
}

/** A quantidade com a unidade do ERP ao lado, quando ele a mandou. */
function quantidadeComUnidade(produto: Produto): string {
  const unidade = produto.unidade_medida === null ? '' : ` ${produto.unidade_medida}`
  return `${quantidadeNaTela(produto.quantidade)}${unidade}`
}

/**
 * A disponibilidade da linha como a tela a escreve: o número em pt-BR com a unidade, ou a frase.
 *
 * O número só vale com `disponibilidade_consultada`: o par vem do serviço junto, e um número sem a
 * marca seria um estoque que ninguém leu agora. O booleano devolvido é o que o estilo lê
 * (`data-nao-consultada`) — e não uma comparação com o texto, que quebraria no dia em que alguém
 * reescrevesse a frase.
 */
function disponibilidadeNaTela(produto: Produto): { texto: string; consultada: boolean } {
  if (!produto.disponibilidade_consultada || produto.disponibilidade === null) {
    return { texto: NAO_CONSULTADA, consultada: false }
  }
  const unidade = produto.unidade_medida === null ? '' : ` ${produto.unidade_medida}`
  return { texto: `${quantidadeNaTela(produto.disponibilidade)}${unidade}`, consultada: true }
}

/** A célula da disponibilidade na tabela de mesa. */
function CelulaDeDisponibilidade({ produto }: { produto: Produto }) {
  const { texto, consultada } = disponibilidadeNaTela(produto)
  return <td data-nao-consultada={!consultada}>{texto}</td>
}

/** O mesmo par na árvore de celular, com o rótulo em cima. */
function DisponibilidadeDaLinha({ produto }: { produto: Produto }) {
  const { texto, consultada } = disponibilidadeNaTela(produto)
  return (
    <span className="produtos__par" data-campo="disponivel">
      <span className="produtos__rotulo">{ROTULOS.disponivel}</span>
      <span className="produtos__campo" data-nao-consultada={!consultada}>
        {texto}
      </span>
    </span>
  )
}

/**
 * O valor **estabilizado**: ele só acompanha o que foi digitado depois de a digitação parar.
 *
 * Ela mora aqui, e não num utilitário compartilhado, porque este é o único lugar do SPA em que uma
 * tecla vira chamada a um sistema de terceiro — as demais buscas do CRM leem o próprio banco. Um
 * helper genérico convidaria a pendurar nele buscas que não precisam esperar.
 *
 * O `clearTimeout` da limpeza é o que impede a tecla anterior de disparar depois da seguinte: sem
 * ele, cada `useEffect` deixaria o seu relógio correndo e o debounce só atrasaria as chamadas em
 * vez de fundi-las.
 */
function useEstabilizado<T>(valor: T, esperaEmMs: number): T {
  const [estabilizado, setEstabilizado] = useState(valor)

  useEffect(() => {
    const relogio = setTimeout(() => {
      setEstabilizado(valor)
    }, esperaEmMs)
    return () => {
      clearTimeout(relogio)
    }
  }, [valor, esperaEmMs])

  return estabilizado
}

/**
 * Diz se a quantidade digitada é aceitável — número finito e maior que zero.
 *
 * A regra é a do contrato (`gt=0`, quatro casas) e a do `CHECK` da coluna, e ela é conferida aqui
 * para que o `422` genérico não seja gasto: o campo vazio manda `""` e o serviço responde "os dados
 * enviados são inválidos", sem dizer qual deles.
 */
function quantidadeAceitavel(bruta: string): boolean {
  const numero = Number(bruta)
  return bruta.trim() !== '' && Number.isFinite(numero) && numero > 0
}

/**
 * Uma célula de dinheiro da tabela de mesa — e o `data-recortado` é o que a faz caber.
 *
 * As colunas numéricas são estreitas e `nowrap`; isso vale para um número, e **não** vale para a
 * frase que a SDR recebe no lugar dele. O booleano vem do view-model, e não de uma comparação com o
 * texto de tela: estilo decidido por igualdade de frase quebra em silêncio no dia em que alguém a
 * reescrever. É a mesma célula do cartão de Orçamento.
 */
function CelulaDeDinheiro({ valor }: { valor: string | null | undefined }) {
  const { texto, recortado } = textoDoValor(valor)
  return <td data-recortado={recortado}>{texto}</td>
}

/** O mesmo par na árvore de celular, onde cada campo já tem o rótulo em cima do valor. */
function DinheiroDaLinha({
  rotulo,
  valor,
  campo,
}: {
  rotulo: string
  valor: string | null | undefined
  campo: 'preco' | 'total'
}) {
  const { texto, recortado } = textoDoValor(valor)
  return (
    <span className="produtos__par" data-campo={campo}>
      <span className="produtos__rotulo">{rotulo}</span>
      <span className="produtos__campo" data-recortado={recortado}>
        {texto}
      </span>
    </span>
  )
}

export function ProdutosDoNegocio({
  negocioId,
  fechado = false,
  somenteLeitura = false,
  excluido = false,
}: {
  negocioId: string
  /**
   * O Negócio já foi ganho ou perdido — e aí a escrita some, com a razão ao lado.
   *
   * Ele chega da **ficha**, que já leu o `status`: uma segunda leitura aqui gastaria uma consulta
   * para saber o que a tela em volta já sabe, e as duas poderiam discordar por um quadro de
   * renderização. O padrão é `false` para que o cartão continue montável fora da ficha.
   */
  fechado?: boolean
  /**
   * Quem lê o Negócio só pela tarefa aberta (13.14c, DW-527): a escrita some como no fechado, sem a
   * frase dele — a nota da ficha explica.
   */
  somenteLeitura?: boolean
  /**
   * O Negócio está sendo excluído (13.14b): a leitura é desligada — os Produtos morrem com ele pela
   * cascata, e a invalidação do prefixo do quadro no `onSuccess` da exclusão (com o modal ainda
   * montado) faria o refetch receber `404`. O mesmo molde da ficha e das RATs.
   */
  excluido?: boolean
}) {
  const ehCelular = useEhCelular()
  const [cartao, estreito] = useCartaoEstreito()
  const emBlocos = ehCelular || estreito
  const consulta = useProdutos(negocioId, { habilitada: !excluido })
  const acrescimo = useAcrescentarProduto(negocioId)
  const remocao = useRemoverProduto(negocioId)
  const relerOValor = useRelerOValorDoNegocio()

  const [codigo, setCodigo] = useState('')
  const [nome, setNome] = useState('')
  const [quantidade, setQuantidade] = useState(QUANTIDADE_PADRAO)

  const idDoCodigo = useId()
  const idDoNome = useId()
  const idDaQuantidade = useId()
  const idDaAjuda = useId()
  const idDaAjudaDaQuantidade = useId()

  /*
   * O filtro é **estabilizado** antes de virar chave de consulta: cada `queryKey` nova é uma ida ao
   * Carga Máquina, e sem isto digitar "parafuso" custaria sete chamadas ao fornecedor (FR-46).
   */
  const digitado: FiltroDeMaterial = {
    codigo: codigo.trim(),
    nome: nome.trim(),
  }
  const filtroEstavel = useEstabilizado(
    `${digitado.codigo ?? ''}\u0000${digitado.nome ?? ''}`,
    ESPERA_DA_BUSCA_EM_MS,
  )
  const [codigoEstavel, nomeEstavel] = filtroEstavel.split('\u0000')
  const filtro: FiltroDeMaterial = { codigo: codigoEstavel, nome: nomeEstavel }

  //: Um dos dois basta, e cada um vale por si: a rota exige **ao menos** um preenchido, e mandar os
  //: dois é uma busca mais estreita (o ERP os combina por E), e não uma busca mais larga.
  const maiorFiltro = Math.max((filtro.codigo ?? '').length, (filtro.nome ?? '').length)
  const semEscrita = fechado || somenteLeitura
  const podeBuscar = maiorFiltro >= MINIMO_PARA_BUSCAR && !semEscrita
  const busca = useBuscaDeMateriais(negocioId, filtro, { habilitada: podeBuscar })

  const lista = consulta.data?.itens ?? []
  const encontrados = busca.data?.itens ?? []
  const quantidadeValida = quantidadeAceitavel(quantidade)

  /*
   * **A reconciliação do valor da ficha**, e ela existe por uma corrida de uma volta só.
   *
   * A ficha e os Produtos são lidos em paralelo, e é a leitura dos Produtos que faz o serviço reler
   * os preços no ERP e regravar as linhas (AD-19). Na primeira abertura depois de o preço mudar lá,
   * a ficha já respondeu com o total **de antes**: o cartão mostra o número novo e o cabeçalho, o
   * velho — na mesma tela.
   *
   * O gatilho é o **total observado**, e não cada resposta: com o total igual ao da última
   * reconciliação, nada é invalidado. É isso que mantém a releitura em uma volta em vez de virar um
   * laço — e `useRelerOValorDoNegocio` não toca a chave dos Produtos, que é a outra metade da mesma
   * garantia.
   *
   * Para quem não vê dinheiro o campo nem chega (`undefined` sempre), e a reconciliação não roda:
   * não há número a conciliar em tela nenhuma.
   */
  const totalReconciliado = useRef<string | null | undefined>(undefined)
  const total = consulta.data?.valor
  useEffect(() => {
    if (fechado || total === undefined || totalReconciliado.current === total) {
      return
    }
    totalReconciliado.current = total
    void relerOValor()
  }, [fechado, total, relerOValor])

  function acrescentar(material: MaterialDoErp) {
    acrescimo.mutate(
      { codigo: material.codigo, quantidade },
      {
        onSuccess: () => {
          //: Os campos são limpos no sucesso, e é isso que fecha a lista de resultados: o Produto
          //: escolhido acabou de entrar na tabela acima, e deixar o resultado na tela convidaria ao
          //: segundo clique — que o serviço trataria como correção de quantidade, calado.
          setCodigo('')
          setNome('')
          //: E a quantidade volta ao padrão. Sem isto, o próximo Produto herdaria a quantidade do
          //: anterior **em silêncio**: quem acrescentou 40 metros de cabo e depois um conector
          //: acrescentaria 40 conectores sem ter digitado nada.
          setQuantidade(QUANTIDADE_PADRAO)
        },
      },
    )
  }

  const corpoDaLista = consulta.isError ? (
    /* Verbatim: a recusa do serviço é a que sabe explicar o caso — inclusive a do FR-47. */
    <p className="ficha__erro" role="alert">
      {mensagemDe(consulta.error)}
    </p>
  ) : /* `|| consulta.data === undefined` fecha o terceiro estado: sem rede, a consulta fica
        **pausada** — nem erro, nem `isLoading` —, e sem esta metade a tela desenharia o estado
        vazio como se o Negócio não tivesse produto. */
  consulta.isLoading || consulta.data === undefined ? (
    <p className="ficha__ajuda" role="status">
      {CARREGANDO}
    </p>
  ) : lista.length === 0 ? (
    <p className="ficha__vazio">{semEscrita ? VAZIO_SEM_ESCRITA : VAZIO}</p>
  ) : emBlocos ? (
    <ul className="produtos__linhas" aria-label={TITULO}>
      {lista.map((produto) => (
        // A chave é o **código**: ele é a identidade da linha no contrato, e é por ele que a
        // remoção a endereça. O contrato não publica uuid nenhum, de propósito.
        <li className="produtos__linha" key={produto.codigo}>
          <span className="produtos__nome">{rotuloDoProduto(produto)}</span>
          <span className="produtos__codigo">{produto.codigo}</span>
          {/* Code review da 13.22 (F20): uma grade fixa de duas colunas — Qtd. e Disponível em
              cima, Preço e Total embaixo —, e o Total nunca sozinho numa linha. */}
          <div className="produtos__numeros" data-testid="numeros-do-produto">
            <span className="produtos__par" data-campo="quantidade">
              <span className="produtos__rotulo">{ROTULOS.quantidade}</span>
              <span className="produtos__campo">{quantidadeComUnidade(produto)}</span>
            </span>
            {fechado ? null : <DisponibilidadeDaLinha produto={produto} />}
            <DinheiroDaLinha rotulo={ROTULOS.preco} valor={produto.preco_unitario} campo="preco" />
            <DinheiroDaLinha rotulo={ROTULOS.total} valor={produto.valor_total} campo="total" />
          </div>
          {semEscrita ? null : (
            <button
              type="button"
              className="produtos__acao"
              disabled={remocao.isPending}
              onClick={() => {
                remocao.mutate(produto.codigo)
              }}
            >
              Remover {rotuloDoProduto(produto)}
            </button>
          )}
        </li>
      ))}
    </ul>
  ) : (
    /* O `aria-label` dá à tabela o mesmo nome que a `<ul>` do celular já tinha: sem ele, quem
       navega por regiões e tabelas encontra uma tabela anônima no meio do cartão. A paridade da
       NFR-1 é de função, e ser alcançável é função. */
    <table className="produtos__tabela" aria-label={TITULO}>
      <thead>
        <tr>
          <th scope="col">{ROTULOS.produto}</th>
          <th scope="col">{ROTULOS.quantidade}</th>
          {/* A disponibilidade só existe no Negócio em andamento: no fechado o serviço nem lê o
              estoque, e uma coluna de "não consultada" ali diria que faltou algo que não se pede. */}
          {fechado ? null : <th scope="col">{ROTULOS.disponivel}</th>}
          <th scope="col">{ROTULOS.preco}</th>
          <th scope="col">{ROTULOS.total}</th>
          {/* A coluna da ação some junto com os botões: um cabeçalho vazio sobre nada seria uma
              coluna que a tabela reserva para um ato que não existe naquele Negócio. */}
          {semEscrita ? null : (
            <th scope="col">
              <span className="ficha__so-para-leitor">Ações</span>
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {lista.map((produto) => (
          <tr key={produto.codigo}>
            <td>
              <span className="produtos__nome">{rotuloDoProduto(produto)}</span>
              <span className="produtos__codigo">{produto.codigo}</span>
            </td>
            <td>{quantidadeComUnidade(produto)}</td>
            {fechado ? null : <CelulaDeDisponibilidade produto={produto} />}
            <CelulaDeDinheiro valor={produto.preco_unitario} />
            <CelulaDeDinheiro valor={produto.valor_total} />
            {semEscrita ? null : (
              <td>
                {/* O nome entra no rótulo do botão porque a tabela tem uma linha por Produto: sem
                    ele, quem navega por controles ouviria "Remover" cinco vezes seguidas, sem
                    saber o que cada um remove. */}
                <button
                  type="button"
                  className="produtos__acao"
                  disabled={remocao.isPending}
                  onClick={() => {
                    remocao.mutate(produto.codigo)
                  }}
                >
                  Remover {rotuloDoProduto(produto)}
                </button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  )

  const resultados = !podeBuscar ? null : busca.isError ? (
    /* O `503` do FR-47 chega aqui **verbatim**: a tela não reescreve a frase do serviço. */
    <p className="ficha__erro" role="alert">
      {mensagemDe(busca.error)}
    </p>
  ) : busca.isLoading || busca.data === undefined ? (
    <p className="ficha__ajuda" role="status">
      {BUSCANDO}
    </p>
  ) : encontrados.length === 0 ? (
    <p className="ficha__vazio">{SEM_RESULTADO}</p>
  ) : (
    <ul className="produtos__resultados" aria-label="Resultados da busca">
      {encontrados.map((material) => {
        const preco = textoDoValor(material.preco)
        const unidade = material.unidade_medida === null ? '' : ` · ${material.unidade_medida}`
        return (
          <li className="produtos__resultado" key={material.codigo}>
            <span className="produtos__nome">{rotuloDoProduto(material)}</span>
            <span className="produtos__codigo">{`${material.codigo}${unidade}`}</span>
            {/* O preço aparece como **texto**, aqui como na tabela: não há `input` de preço em
                lugar nenhum desta tela, e é a mesma função de recorte da ficha que o escreve. */}
            <span className="produtos__preco" data-recortado={preco.recortado}>
              {preco.texto}
            </span>
            {/* Desabilitado enquanto a quantidade não for `> 0`: o campo vazio mandaria
                `{"quantidade": ""}` e receberia o `422` genérico do envelope, que não diz qual
                campo está errado. A frase acima é a que explica, e o `aria-describedby` do campo a
                liga a ele. */}
            <button
              type="button"
              className="ficha__botao ficha__botao--primario"
              disabled={acrescimo.isPending || !quantidadeValida}
              onClick={() => {
                acrescentar(material)
              }}
            >
              Acrescentar {rotuloDoProduto(material)}
            </button>
          </li>
        )
      })}
    </ul>
  )

  return (
    <section className="ficha__card" aria-labelledby={ID_DO_TITULO} ref={cartao}>
      <div className="ficha__card-topo">
        <span className="ficha__card-titulo" id={ID_DO_TITULO}>
          {TITULO}
        </span>
        <span className="ficha__selo">{SELO}</span>
      </div>
      <div className="ficha__card-corpo">
        {corpoDaLista}

        {fechado ? (
          <p className="ficha__ajuda">{NEGOCIO_FECHADO}</p>
        ) : somenteLeitura ? null : (
          <div className="produtos__busca">
            <div className="produtos__campos">
              {/* **Dois campos, e não um.** `GET /material` combina `codigo` e `nome` por E: o
                  mesmo texto nos dois pede o Material cujo código *e* nome o contenham, e a busca
                  nunca acha nada. Cada um vai sozinho na query, que é o contrato da rota. */}
              <div className="produtos__campo-de-entrada">
                <label className="ficha__linha-rotulo" htmlFor={idDoCodigo}>
                  {ROTULO_DO_CODIGO}
                </label>
                <input
                  id={idDoCodigo}
                  className="ficha__entrada"
                  type="search"
                  autoComplete="off"
                  aria-describedby={idDaAjuda}
                  value={codigo}
                  onChange={(evento) => {
                    setCodigo(evento.target.value)
                  }}
                />
              </div>
              <div className="produtos__campo-de-entrada">
                <label className="ficha__linha-rotulo" htmlFor={idDoNome}>
                  {ROTULO_DO_NOME}
                </label>
                <input
                  id={idDoNome}
                  className="ficha__entrada"
                  type="search"
                  autoComplete="off"
                  aria-describedby={idDaAjuda}
                  value={nome}
                  onChange={(evento) => {
                    setNome(evento.target.value)
                  }}
                />
              </div>
              <div className="produtos__campo-de-entrada produtos__campo-de-entrada--curto">
                <label className="ficha__linha-rotulo" htmlFor={idDaQuantidade}>
                  {ROTULO_DA_QUANTIDADE}
                </label>
                {/* `min` e `step` espelham o contrato: quantidade é `> 0` e aceita até quatro
                    casas. A recusa que vale continua sendo a do serviço — esta é a que evita
                    gastar a ida à rede com um `422` genérico que não diz qual campo. */}
                <input
                  id={idDaQuantidade}
                  className="ficha__entrada"
                  type="number"
                  min="0.0001"
                  step="0.0001"
                  aria-describedby={quantidadeValida ? undefined : idDaAjudaDaQuantidade}
                  aria-invalid={quantidadeValida ? undefined : true}
                  value={quantidade}
                  onChange={(evento) => {
                    setQuantidade(evento.target.value)
                  }}
                />
              </div>
            </div>
            {/* A ajuda é ligada aos dois campos de busca por `aria-describedby`: solta, ela é um
                parágrafo que quem navega por controles nunca ouve. */}
            <p className="ficha__ajuda" id={idDaAjuda}>
              {AJUDA_DA_BUSCA}
            </p>
            {quantidadeValida ? null : (
              <p className="ficha__ajuda" id={idDaAjudaDaQuantidade}>
                {QUANTIDADE_INVALIDA}
              </p>
            )}
            {maiorFiltro > 0 && maiorFiltro < MINIMO_PARA_BUSCAR ? (
              <p className="ficha__ajuda">{BUSCA_CURTA}</p>
            ) : null}
            {resultados}
            {acrescimo.isError ? (
              /* Verbatim: `produto_nao_encontrado`, `teto_de_linhas_do_negocio`, o `409` do
                 Negócio fechado e o `503` do FR-47 chegam com a frase do serviço. */
              <p className="ficha__erro" role="alert">
                {mensagemDe(acrescimo.error)}
              </p>
            ) : null}
            {remocao.isError ? (
              <p className="ficha__erro" role="alert">
                {mensagemDe(remocao.error)}
              </p>
            ) : null}
          </div>
        )}
      </div>
    </section>
  )
}
